import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotificationStore } from '@/store/notificationStore';
import type { GlobalNotification, NotificationCategory } from '@/types/notification.types';
import { X, Check, CheckCheck, Trash2, Bell, Sparkles } from 'lucide-react';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

export function NotificationDropdown({ isOpen, onClose }: NotificationDropdownProps) {
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [activeTab, setActiveTab] = useState<'all' | NotificationCategory>('all');

  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    markCategoryAsRead,
    removeNotification,
    clearAll,
  } = useNotificationStore();

  // Format time ago
  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const then = new Date(timestamp);
    const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return then.toLocaleDateString();
  };

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
    }

    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredNotifications =
    activeTab === 'all'
      ? notifications
      : notifications.filter((n) => n.category === activeTab);

  const categories: Array<{
    value: 'all' | NotificationCategory;
    label: string;
    icon: string;
    gradient: string;
  }> = [
    { value: 'all', label: 'All', icon: '📋', gradient: 'from-purple-500 to-pink-500' },
    { value: 'matches', label: 'Matches', icon: '⚔️', gradient: 'from-red-500 to-orange-500' },
    { value: 'tournaments', label: 'Tournaments', icon: '🏆', gradient: 'from-yellow-500 to-amber-500' },
    { value: 'ladder', label: 'Ladder', icon: '📊', gradient: 'from-green-500 to-emerald-500' },
    { value: 'gathers', label: 'Gathers', icon: '👥', gradient: 'from-blue-500 to-cyan-500' },
    { value: 'friends', label: 'Friends', icon: '👤', gradient: 'from-indigo-500 to-purple-500' },
    { value: 'messages', label: 'Messages', icon: '💬', gradient: 'from-pink-500 to-rose-500' },
    { value: 'system', label: 'System', icon: '⚙️', gradient: 'from-gray-500 to-slate-500' },
  ];

  const handleNotificationClick = (notification: GlobalNotification) => {
    markAsRead(notification.id);

    // Special handling for Active Match notifications - open popup window
    if (notification.type === 'ACTIVE_MATCH' && notification.metadata) {
      const matchId = (notification.metadata as any).match_id || (notification.metadata as any).id;
      if (matchId) {
        // Open Match Lobby in popup window
        const width = 900;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const matchWindow = window.open(
          `/client/matchmaking?match=${matchId}`,
          'MatchLobby',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,menubar=no,status=yes,resizable=yes`
        );

        if (matchWindow) {
          matchWindow.focus();
        } else {
          // Fallback if popup blocked
          navigate(`/client/matchmaking?match=${matchId}`);
        }

        onClose();
        return;
      }
    }

    // Default behavior for other notifications
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
      onClose();
    }
  };

  const getPriorityStyles = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return {
          border: 'border-l-4 border-red-500',
          bg: 'bg-red-50 dark:bg-red-900/10',
          icon: '🔴'
        };
      case 'high':
        return {
          border: 'border-l-4 border-orange-500',
          bg: 'bg-orange-50 dark:bg-orange-900/10',
          icon: '🟠'
        };
      case 'normal':
        return {
          border: 'border-l-4 border-blue-500',
          bg: 'bg-blue-50 dark:bg-blue-900/10',
          icon: '🔵'
        };
      default:
        return {
          border: 'border-l-4 border-gray-300',
          bg: 'bg-gray-50 dark:bg-gray-800',
          icon: '⚪'
        };
    }
  };

  const getCategoryIcon = (category: NotificationCategory | 'all') => {
    return categories.find(c => c.value === category)?.icon || '📋';
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/40 backdrop-blur-sm z-40 animate-in fade-in duration-200"
        onClick={onClose}
      />

      {/* Notification Panel */}
      <div
        ref={dropdownRef}
        style={{
          position: 'fixed',
          top: '90px',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '520px',
          maxWidth: '95vw',
          maxHeight: '80vh',
          zIndex: 9999
        }}
        className="bg-white dark:bg-gray-900 border-2 border-gray-200 dark:border-gray-700 rounded-3xl shadow-2xl overflow-hidden animate-in slide-in-from-top-4 duration-300"
      >
        {/* Header with Gradient */}
        <div className="relative bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 px-6 py-5">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <Bell className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-0.5">
                  Notifications
                </h3>
                <p className="text-sm text-white/90 font-medium">
                  {unreadCount > 0 ? `${unreadCount} unread message${unreadCount > 1 ? 's' : ''}` : 'All caught up! 🎉'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => {
                    if (activeTab === 'all') {
                      markAllAsRead();
                    } else {
                      markCategoryAsRead(activeTab);
                    }
                  }}
                  className="p-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                  title="Mark all as read"
                >
                  <CheckCheck className="h-5 w-5 text-white" />
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl transition-all duration-200 hover:scale-105 active:scale-95"
                title="Close"
              >
                <X className="h-5 w-5 text-white" />
              </button>
            </div>
          </div>
        </div>

        {/* Category Pills - Horizontal Wrap */}
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b-2 border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap gap-1.5">
            {categories.map((cat) => {
              const isActive = activeTab === cat.value;
              const categoryUnread = cat.value === 'all'
                ? unreadCount
                : notifications.filter(n => n.category === cat.value && !n.read).length;

              return (
                <button
                  key={cat.value}
                  onClick={() => setActiveTab(cat.value)}
                  className={`
                    inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold
                    transition-all duration-200 hover:scale-105 active:scale-95
                    ${isActive
                      ? `bg-gradient-to-r ${cat.gradient} text-white shadow-md`
                      : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600'
                    }
                  `}
                >
                  <span>{cat.icon}</span>
                  <span>{cat.label}</span>
                  {categoryUnread > 0 && (
                    <span className={`
                      ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold leading-none
                      ${isActive
                        ? 'bg-white/30 text-white'
                        : 'bg-red-500 text-white'
                      }
                    `}>
                      {categoryUnread > 99 ? '99+' : categoryUnread}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Notifications List */}
        <div className="overflow-y-auto max-h-[calc(80vh-200px)] bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-900">
          {filteredNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6">
              <div className="p-8 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-3xl mb-6">
                <Bell className="h-20 w-20 text-gray-400 dark:text-gray-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                No notifications
              </h4>
              <p className="text-base text-gray-500 dark:text-gray-400 text-center max-w-xs">
                {activeTab === 'all'
                  ? "You're all caught up! No new notifications."
                  : `No ${categories.find(c => c.value === activeTab)?.label.toLowerCase()} notifications.`
                }
              </p>
            </div>
          ) : (
            <div className="divide-y-2 divide-gray-200 dark:divide-gray-700">
              {filteredNotifications.map((notification) => {
                const priorityStyles = getPriorityStyles(notification.priority);
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`
                      group relative px-6 py-5 cursor-pointer transition-all duration-200
                      hover:bg-white dark:hover:bg-gray-800
                      ${!notification.read ? priorityStyles.bg : 'bg-transparent'}
                      ${priorityStyles.border}
                    `}
                  >
                    <div className="flex items-start gap-5">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        <div className={`
                          p-4 rounded-2xl bg-gradient-to-br shadow-lg
                          ${categories.find(c => c.value === notification.category)?.gradient}
                          transform transition-transform group-hover:scale-110
                        `}>
                          <span className="text-3xl">
                            {getCategoryIcon(notification.category)}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 pt-1">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <div className="flex items-center gap-2.5">
                            <h4 className="text-base font-bold text-gray-900 dark:text-white">
                              {notification.title}
                            </h4>
                            {!notification.read && (
                              <span className="flex h-2.5 w-2.5">
                                <span className="animate-ping absolute h-2.5 w-2.5 rounded-full bg-blue-500 opacity-75"></span>
                                <span className="relative rounded-full h-2.5 w-2.5 bg-blue-500"></span>
                              </span>
                            )}
                          </div>
                          <span className="text-sm flex items-center gap-1">
                            {priorityStyles.icon}
                          </span>
                        </div>

                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2 leading-relaxed">
                          {notification.message}
                        </p>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500 dark:text-gray-500 font-semibold">
                            {formatTimeAgo(notification.timestamp)}
                          </span>
                          {notification.actionLabel && (
                            <span className="text-sm font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent flex items-center gap-1.5">
                              {notification.actionLabel}
                              <span className="text-base">→</span>
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex-shrink-0 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity pt-1">
                        {!notification.read && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            className="p-2.5 rounded-xl bg-green-500/10 hover:bg-green-500/20 text-green-600 dark:text-green-400 transition-all hover:scale-110"
                            title="Mark as read"
                          >
                            <Check className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notification.id);
                          }}
                          className="p-2.5 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 transition-all hover:scale-110"
                          title="Remove"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="px-6 py-5 bg-white dark:bg-gray-800 border-t-2 border-gray-200 dark:border-gray-700">
            <button
              onClick={() => {
                if (confirm('Clear all notifications? This cannot be undone.')) {
                  clearAll();
                  onClose();
                }
              }}
              className="w-full py-3.5 px-5 bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white font-bold rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl text-base"
            >
              <div className="flex items-center justify-center gap-2.5">
                <Trash2 className="h-5 w-5" />
                Clear All Notifications
              </div>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
